import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CodeIcon from "@mui/icons-material/Code";
import VideoIcon from "../assets/svgImages/videoIcon.svg";
import TasklistIcon from "../assets/svgImages/taskListIcon.svg";
import ReadingMaterial from "../assets/svgImages/readingMaterialIcon.svg";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import SessionTracking from "./Sessions/SessionTracking";
import api from "../api";
import { ManageQuestionsModal } from "components/QuestionLibrary";
import { ViewAssignmentsModal } from "components/AssignmentLibrary";
import { AssessmentsManager, AssignmentsManager } from "./ModuleTasks";
import { moduleReportPath } from "./Submissions/submissionsApi";
import StudyMaterialsPanel from "./StudyMaterials/StudyMaterialsPanel";

const BatchModulesCard = ({ batchData, onRefresh }) => {
  const userProfile = useSelector((state) => state.userProfile);
  const isMentor = userProfile?.data?.role === "mentor";
  const navigate = useNavigate();
  const { id } = useParams();
  const [expandedId, setExpandedId] = useState(null);
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [selectedTopicForQuestions, setSelectedTopicForQuestions] = useState(null);
  const [isAssignmentsModalOpen, setIsAssignmentsModalOpen] = useState(false);
  const [selectedTopicForAssignments, setSelectedTopicForAssignments] = useState(null);

  const { batch, topics } = batchData || {};

  const toggleAccordion = (topicId) => {
    setExpandedId(expandedId === topicId ? null : topicId);
  };

  const handleToggleActive = async (event, topic) => {
    event.stopPropagation();
    try {
      await api.patch(`api/course-template/${topic.id}/toggle-status/`, {
        is_active: !topic.is_active,
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to update module status:", err);
    }
  };

  const handleToggleComplete = async (event, topic) => {
    event.stopPropagation();
    try {
      await api.patch(`api/course-template/${topic.id}/toggle-status/`, {
        is_completed: !topic.is_completed,
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Failed to update module status:", err);
    }
  };

  if (!topics || topics.length === 0) {
    return (
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 6,
        backgroundColor: "#f9fafb",
        borderRadius: "12px",
        border: "2px dashed #e5e7eb",
      }}>
        <Typography sx={{ fontFamily: "Be Vietnam Pro", color: "#374151", fontSize: "18px", fontWeight: 500, mb: 1 }}>
          No modules yet
        </Typography>
        <Typography sx={{ fontFamily: "Be Vietnam Pro", color: "#6b7280", fontSize: "14px", textAlign: "center", mb: 2 }}>
          Add modules to this cohort from the Content Library to get started.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(`/dashboard/content-library`)}
          sx={{
            backgroundColor: "#0172A6",
            textTransform: "none",
            fontFamily: "Be Vietnam Pro",
            "&:hover": {
              backgroundColor: "#015a84",
            },
          }}
        >
          Go to Content Library
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "25px",
          color: "#202124",
          fontFamily: "Be Vietnam Pro",
          marginBottom: "16px",
        }}
      >
        Course Modules
      </Typography>

      {topics.map((topic, index) => (
        <Grid item xs={12} mb={3} key={topic.id}>
          <Accordion
            sx={{
              "& .MuiAccordionSummary-expandIcon": { display: "none" },
              padding: "8px",
              borderRadius: "10px",
            }}
            p={1}
            expanded={expandedId === topic.id}
            onChange={() => toggleAccordion(topic.id)}
          >
            <AccordionSummary>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    sx={{
                      color: "#868686",
                      fontWeight: 400,
                      fontSize: "14px",
                      fontFamily: "Be Vietnam Pro",
                    }}
                    gutterBottom
                  >
                    MODULE {index + 1}
                  </Typography>
                  <Box display="flex" gap="4px">
                    <Tooltip title={topic.is_active ? "Mark Inactive" : "Mark Active"}>
                      {topic.is_active ? (
                        <PlayCircleOutlineIcon
                          sx={{
                            color: "#22c55e",
                            width: "20px",
                            height: "20px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => handleToggleActive(e, topic)}
                        />
                      ) : (
                        <PauseCircleOutlineIcon
                          sx={{
                            color: "#6b7280",
                            width: "20px",
                            height: "20px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => handleToggleActive(e, topic)}
                        />
                      )}
                    </Tooltip>
                    <Tooltip title={topic.is_completed ? "Mark Incomplete" : "Mark Complete"}>
                      {topic.is_completed ? (
                        <CheckCircleOutlineIcon
                          sx={{
                            color: "green",
                            width: "20px",
                            height: "20px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => handleToggleComplete(e, topic)}
                        />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{
                            color: "#6b7280",
                            width: "20px",
                            height: "20px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => handleToggleComplete(e, topic)}
                        />
                      )}
                    </Tooltip>
                  </Box>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "18px",
                    color: "#202124",
                    fontFamily: "Be Vietnam Pro",
                  }}
                >
                  {topic.name}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                  mt={2}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontFamily: "Be Vietnam Pro",
                      fontSize: "12px",
                      borderRadius: "8px",
                    }}
                  >
                    {expandedId === topic.id ? "Hide Details" : "Show Details"}
                  </Button>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Session Tracking */}
                <Grid item xs={12}>
                  <Accordion
                    TransitionProps={{ unmountOnExit: true }}
                    sx={{
                      ".MuiAccordionSummary-content": { margin: "0px" },
                      padding: "0px",
                      borderRadius: "10px",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      sx={{
                        backgroundColor: "#E5F1F6",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        py: 0,
                        px: 1,
                        minHeight: "37px !important",
                      }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <img src={VideoIcon} alt="Session Tracking" style={{ marginRight: 8 }} />
                      <Typography
                        sx={{
                          color: "#202124",
                          fontSize: "14px",
                          fontFamily: "Be Vietnam Pro",
                        }}
                      >
                        Session Tracking
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <SessionTracking moduleId={topic.id} basePath={`/dashboard/batch/${batch?.id}/sessions`} />
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                {/* Reading Material */}
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    backgroundColor="#E5F1F6"
                    p={1}
                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#d0e5ed" } }}
                    onClick={() => navigate(`/dashboard/batch/${batch?.id}/reading-material`, {
                      state: { topic_template_id: topic.id }
                    })}
                  >
                    <img src={ReadingMaterial} alt="Reading" style={{ marginRight: 8 }} />
                    <Typography
                      sx={{
                        color: "#202124",
                        fontSize: "14px",
                        fontFamily: "Be Vietnam Pro",
                      }}
                    >
                      Reading material / FlowState Content
                    </Typography>
                  </Box>
                </Grid>

                {/* Supplementary Study Materials (download-only extras) */}
                <Grid item xs={12}>
                  <StudyMaterialsPanel
                    moduleId={topic.id}
                    moduleName={topic.name}
                    canEdit={isMentor}
                    initialCount={topic.study_materials_count || 0}
                  />
                </Grid>

                {/* Task Lists */}
                <Grid item xs={12}>
                  <Accordion
                    sx={{
                      ".MuiAccordionSummary-content": { margin: "0px" },
                      padding: "0px",
                      borderRadius: "10px",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      sx={{
                        backgroundColor: "#E5F1F6",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        py: 0,
                        px: 1,
                        minHeight: "37px !important",
                      }}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <img src={TasklistIcon} alt="Task List" style={{ marginRight: 8 }} />
                      <Typography
                        sx={{
                          color: "#202124",
                          fontSize: "14px",
                          fontFamily: "Be Vietnam Pro",
                        }}
                      >
                        Task Lists
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {/* Assignment Row */}
                      <Box
                        sx={{
                          backgroundColor: "#e3f0f9",
                          border: "1px solid #9fcdea",
                          ml: 2,
                          p: 1.5,
                          width: "96%",
                          my: 1,
                          fontSize: "15px",
                          fontFamily: "Be Vietnam Pro",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          borderRadius: "8px",
                        }}
                      >
                        <AssignmentIcon sx={{ fontSize: 18, color: "#0172A6" }} />
                        <Typography sx={{ fontWeight: 600, fontSize: "15px", flex: 1 }}>
                          Assignment
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTopicForAssignments(topic);
                            setIsAssignmentsModalOpen(true);
                          }}
                          sx={{
                            fontSize: "12px",
                            textTransform: "none",
                            borderColor: "#9fcdea",
                            color: "#0f6ca8",
                            background: "#fff",
                            "&:hover": {
                              borderColor: "#0f6ca8",
                              background: "#f0f7fc",
                            },
                          }}
                        >
                          {isMentor ? "Manage assignments" : "View assignments"}
                        </Button>
                        {isMentor && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(moduleReportPath("template", topic.id, "assignments"));
                          }}
                          sx={{
                            fontSize: "12px",
                            textTransform: "none",
                            borderColor: "#9fcdea",
                            color: "#0f6ca8",
                            background: "#fff",
                            "&:hover": {
                              borderColor: "#0f6ca8",
                              background: "#f0f7fc",
                            },
                          }}
                        >
                          Submissions
                        </Button>
                        )}
                      </Box>

                      {/* Assessment Row */}
                      <Box
                        sx={{
                          backgroundColor: "#e3f0f9",
                          border: "1px solid #9fcdea",
                          ml: 2,
                          p: 1.5,
                          width: "96%",
                          fontSize: "15px",
                          fontFamily: "Be Vietnam Pro",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          borderRadius: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <CodeIcon sx={{ fontSize: 18, color: "#0172A6" }} />
                        <Typography sx={{ fontWeight: 600, fontSize: "15px", flex: 1 }}>
                          Assessment
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTopicForQuestions(topic);
                            setIsQuestionsModalOpen(true);
                          }}
                          sx={{
                            fontSize: "12px",
                            textTransform: "none",
                            borderColor: "#9fcdea",
                            color: "#0f6ca8",
                            background: "#fff",
                            "&:hover": {
                              borderColor: "#0f6ca8",
                              background: "#f0f7fc",
                            },
                          }}
                        >
                          {isMentor ? "Manage assessments" : "View questions"}
                        </Button>
                        {isMentor && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(moduleReportPath("template", topic.id, "assessments"));
                          }}
                          sx={{
                            fontSize: "12px",
                            textTransform: "none",
                            borderColor: "#9fcdea",
                            color: "#0f6ca8",
                            background: "#fff",
                            "&:hover": {
                              borderColor: "#0f6ca8",
                              background: "#f0f7fc",
                            },
                          }}
                        >
                          Results
                        </Button>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ))}

      {/* Mentors manage the batch module's assessments and assignments; everyone in the batch gets them */}
      <AssessmentsManager
        open={isMentor && isQuestionsModalOpen}
        onClose={() => {
          setIsQuestionsModalOpen(false);
          setSelectedTopicForQuestions(null);
        }}
        kind="template"
        moduleId={selectedTopicForQuestions?.id}
        moduleName={selectedTopicForQuestions?.name}
      />
      <AssignmentsManager
        open={isMentor && isAssignmentsModalOpen}
        onClose={() => {
          setIsAssignmentsModalOpen(false);
          setSelectedTopicForAssignments(null);
        }}
        kind="template"
        moduleId={selectedTopicForAssignments?.id}
        moduleName={selectedTopicForAssignments?.name}
      />

      {/* View Questions Modal */}
      <ManageQuestionsModal
        isOpen={!isMentor && isQuestionsModalOpen}
        onClose={() => {
          setIsQuestionsModalOpen(false);
          setSelectedTopicForQuestions(null);
        }}
        topicId={selectedTopicForQuestions?.id}
        topicName={selectedTopicForQuestions?.name}
        isCourseTemplate={true}
      />

      {/* View Assignments Modal */}
      <ViewAssignmentsModal
        isOpen={!isMentor && isAssignmentsModalOpen}
        onClose={() => {
          setIsAssignmentsModalOpen(false);
          setSelectedTopicForAssignments(null);
        }}
        topicId={selectedTopicForAssignments?.id}
        topicName={selectedTopicForAssignments?.name}
        isCourseTemplate={true}
      />
    </>
  );
};

export default BatchModulesCard;
